/**
 * @file The admin-managed label vocabulary and the closed palette it draws
 * from. Consumers: `OrganizationParameters` holds one set for attachments and
 * one for the communication log; `ArtifactAttachments` entries and every
 * `CommunicationRecord` member carry assignments as bare ids.
 *
 * Ids are opaque and never reused, so an archived label stays resolvable, an
 * orphaned id renders nothing, and every id is safe as a Firestore dotted-path
 * segment. `LabelSet.groups` is a map rather than an array so one group can be
 * written under its own path without shipping its siblings at a stale value,
 * and `groupOrder` carries the order the administrator declared so no renderer
 * ever sorts.
 */

export type LabelId = string;

export type LabelGroupId = string;

/**
 * The colours an administrator may give a label: exactly the presets Ant
 * Design publishes in `antd/es/theme/interface/presetColors`. A preset
 * resolves through the generated light/dark token ramp and emits no inline
 * style, while a non-preset sends `Tag` down a hardcoded-lightness path that
 * ignores the dark algorithm. The order here is the order the picker shows,
 * running the spectrum rather than the library's own listing order. A consumer
 * needing a no-colour option prepends `'default'` itself.
 */
export const LABEL_COLORS = [
    'blue',
    'geekblue',
    'cyan',
    'green',
    'lime',
    'yellow',
    'gold',
    'orange',
    'volcano',
    'red',
    'magenta',
    'pink',
    'purple',
] as const;

export type LabelColor = (typeof LABEL_COLORS)[number];

/**
 * One administrator-defined label. `id` is opaque and immutable; `name` is what
 * the reader sees and may be renamed freely without touching anything carrying
 * the id. `archived` retires it: no picker offers it except on an item already
 * carrying it, and it keeps rendering wherever it is already applied.
 */
export type Label = {
    id: LabelId;
    name: string;
    color: LabelColor;
    archived?: true;
};

/**
 * A named, ordered group of labels. A label belongs to exactly one group.
 * `archived` stops the group offering any of its labels without archiving
 * anything structurally beneath it, so un-archiving restores exactly those
 * labels that were not archived in their own right.
 */
export type LabelGroup = {
    id: LabelGroupId;
    name: string;
    labels: Label[];
    archived?: true;
};

/**
 * One administrator-managed label set. `groups` is keyed by group id so a save
 * can address a single group by a dotted path; `groupOrder` declares the order
 * those groups are shown in.
 */
export type LabelSet = {
    groups: Record<LabelGroupId, LabelGroup>;
    groupOrder: LabelGroupId[];
};
